import React, { useState, useMemo, useCallback } from 'react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { EmptyState } from './EmptyState';
import { FileText, ArrowUpDown, ArrowUp, ArrowDown, Search, ChevronLeft, ChevronRight, Filter, X } from 'lucide-react';

// ============== SORT HELPERS ==============
const compare = (a, b, accessor, dir) => {
  let valA = typeof accessor === 'function' ? accessor(a) : a[accessor];
  let valB = typeof accessor === 'function' ? accessor(b) : b[accessor];
  if (valA == null) valA = '';
  if (valB == null) valB = '';
  if (typeof valA === 'string') valA = valA.toLowerCase();
  if (typeof valB === 'string') valB = valB.toLowerCase();
  if (valA < valB) return dir === 'asc' ? -1 : 1;
  if (valA > valB) return dir === 'asc' ? 1 : -1;
  return 0;
};

// ============== TOOLBAR ==============
export const DataTableToolbar = ({
  searchValue, onSearchChange, searchPlaceholder = 'Search...',
  filters = [], filterValues = {}, onFilterChange,
  totalCount, className
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const hasActiveFilters = Object.values(filterValues).some(v => v && v !== 'all');

  return (
    <div className={cn('space-y-3 mb-4', className)} data-testid="data-table-toolbar">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="pl-9 h-9 text-sm"
            data-testid="dt-search-input"
          />
          {searchValue && (
            <button onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X size={14} />
            </button>
          )}
        </div>
        {filters.length > 0 && (
          <Button variant="outline" size="sm" className="h-9 gap-1.5 shrink-0"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="dt-toggle-filters">
            <Filter size={14} />
            <span className="hidden sm:inline">Filters</span>
            {hasActiveFilters && (
              <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center">
                {Object.values(filterValues).filter(v => v && v !== 'all').length}
              </span>
            )}
          </Button>
        )}
        {totalCount !== undefined && (
          <div className="hidden sm:flex items-center text-xs text-slate-500 px-2 shrink-0">
            {totalCount} result{totalCount !== 1 ? 's' : ''}
          </div>
        )}
      </div>
      {showFilters && filters.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="dt-filter-panel">
          {filters.map((filter) => (
            <div key={filter.key} className="min-w-[150px]">
              <Select value={filterValues[filter.key] || 'all'}
                onValueChange={(val) => onFilterChange(filter.key, val)}>
                <SelectTrigger className="h-8 text-xs bg-white" data-testid={`dt-filter-${filter.key}`}>
                  <SelectValue placeholder={filter.label} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All {filter.label}</SelectItem>
                  {filter.options.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500"
              onClick={() => filters.forEach(f => onFilterChange(f.key, 'all'))}
              data-testid="dt-clear-filters">
              Clear all
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// ============== PAGINATION ==============
const Pagination = ({ page, pageSize, total, onPageChange, onPageSizeChange }) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-100" data-testid="dt-pagination">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <span>Rows per page</span>
        <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
          <SelectTrigger className="h-7 w-[65px] text-xs" data-testid="dt-page-size">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[10, 25, 50, 100].map(s => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-500">
          {start}–{end} of {total}
        </span>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1}
            onClick={() => onPageChange(page - 1)} data-testid="dt-prev-page">
            <ChevronLeft size={14} />
          </Button>
          {totalPages <= 5 ? (
            Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <Button key={p} variant={p === page ? 'default' : 'outline'}
                size="sm" className="h-7 w-7 p-0 text-xs"
                onClick={() => onPageChange(p)} data-testid={`dt-page-${p}`}>
                {p}
              </Button>
            ))
          ) : (
            <>
              <Button variant={page === 1 ? 'default' : 'outline'} size="sm" className="h-7 w-7 p-0 text-xs"
                onClick={() => onPageChange(1)}>1</Button>
              {page > 3 && <span className="text-xs text-slate-400 px-1">...</span>}
              {Array.from({ length: 3 }, (_, i) => {
                const p = Math.min(Math.max(page - 1 + i, 2), totalPages - 1);
                return p;
              }).filter((p, i, arr) => arr.indexOf(p) === i && p > 1 && p < totalPages).map(p => (
                <Button key={p} variant={p === page ? 'default' : 'outline'}
                  size="sm" className="h-7 w-7 p-0 text-xs"
                  onClick={() => onPageChange(p)}>{p}</Button>
              ))}
              {page < totalPages - 2 && <span className="text-xs text-slate-400 px-1">...</span>}
              <Button variant={page === totalPages ? 'default' : 'outline'} size="sm" className="h-7 w-7 p-0 text-xs"
                onClick={() => onPageChange(totalPages)}>{totalPages}</Button>
            </>
          )}
          <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)} data-testid="dt-next-page">
            <ChevronRight size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============== SORTABLE HEADER ==============
const SortHeader = ({ column, sortKey, sortDir, onSort }) => {
  if (!column.sortable) return <span>{column.header}</span>;
  const accessor = column.sortAccessor || column.accessor;
  const isActive = sortKey === accessor;
  return (
    <button className="flex items-center gap-1 hover:text-slate-800 transition-colors group"
      onClick={() => onSort(accessor)}
      data-testid={`dt-sort-${accessor}`}>
      <span>{column.header}</span>
      {isActive ? (
        sortDir === 'asc' ? <ArrowUp size={13} className="text-blue-600" /> : <ArrowDown size={13} className="text-blue-600" />
      ) : (
        <ArrowUpDown size={13} className="text-slate-300 group-hover:text-slate-400" />
      )}
    </button>
  );
};

// ============== MAIN DATA TABLE ==============
export const DataTable = ({
  columns, data, onRowClick,
  emptyTitle = 'No data found', emptyDescription = 'There are no records to display.',
  className,
  // Search
  searchValue = '', onSearchChange, searchPlaceholder = 'Search...',
  searchFields = [],
  // Filters
  filters = [], filterValues = {}, onFilterChange, filterFn,
  // Sorting
  defaultSortKey, defaultSortDir = 'asc',
  // Pagination
  paginated = true, defaultPageSize = 25,
  // Toolbar
  showToolbar = true,
}) => {
  const [sortKey, setSortKey] = useState(defaultSortKey || null);
  const [sortDir, setSortDir] = useState(defaultSortDir);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [internalSearch, setInternalSearch] = useState('');

  const search = onSearchChange !== undefined ? searchValue : internalSearch;
  const setSearch = onSearchChange !== undefined ? onSearchChange : setInternalSearch;

  const handleSort = useCallback((key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  }, [sortKey]);

  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setPage(1);
  }, []);

  // Process data: search → filter → sort → paginate
  const processed = useMemo(() => {
    let result = [...(data || [])];

    // Search
    if (search && searchFields.length > 0) {
      const q = search.toLowerCase();
      result = result.filter(row =>
        searchFields.some(field => {
          const val = typeof field === 'function' ? field(row) : row[field];
          return val && String(val).toLowerCase().includes(q);
        })
      );
    }

    // Filters
    if (filterFn) {
      result = result.filter(row => filterFn(row, filterValues));
    }

    const total = result.length;

    // Sort
    if (sortKey) {
      result.sort((a, b) => compare(a, b, sortKey, sortDir));
    }

    // Paginate
    if (paginated) {
      const start = (page - 1) * pageSize;
      result = result.slice(start, start + pageSize);
    }

    return { rows: result, total };
  }, [data, search, searchFields, filterFn, filterValues, sortKey, sortDir, page, pageSize, paginated]);

  // Reset page when search/filter changes
  const prevSearch = React.useRef(search);
  const prevFilters = React.useRef(filterValues);
  React.useEffect(() => {
    if (prevSearch.current !== search || JSON.stringify(prevFilters.current) !== JSON.stringify(filterValues)) {
      setPage(1);
      prevSearch.current = search;
      prevFilters.current = filterValues;
    }
  }, [search, filterValues]);

  return (
    <div>
      {showToolbar && (
        <DataTableToolbar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder={searchPlaceholder}
          filters={filters}
          filterValues={filterValues}
          onFilterChange={onFilterChange || (() => {})}
          totalCount={processed.total}
        />
      )}

      {processed.total === 0 && !search && !Object.values(filterValues).some(v => v && v !== 'all') ? (
        <EmptyState icon={FileText} title={emptyTitle} description={emptyDescription} />
      ) : processed.total === 0 ? (
        <EmptyState icon={Search} title="No results found" description="Try adjusting your search or filters." />
      ) : (
        <div className={cn('rounded-md border border-slate-200 overflow-x-auto bg-white', className)}>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {columns.map((column, index) => (
                  <TableHead key={index}
                    className="text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                    <SortHeader column={column} sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {processed.rows.map((row, rowIndex) => (
                <TableRow key={rowIndex}
                  className={cn('table-row-hover', onRowClick && 'cursor-pointer')}
                  onClick={() => onRowClick?.(row)}
                  data-testid={`table-row-${rowIndex}`}>
                  {columns.map((column, colIndex) => (
                    <TableCell key={colIndex} className="text-sm text-slate-600">
                      {column.cell ? column.cell(row) : row[column.accessor]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {paginated && processed.total > 0 && (
        <Pagination page={page} pageSize={pageSize} total={processed.total}
          onPageChange={setPage} onPageSizeChange={handlePageSizeChange} />
      )}
    </div>
  );
};

export default DataTable;
